import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ModuleKey, SearchResponse } from '@tww/shared';
import { ROLE_NAMES, initialsOf } from '@tww/shared';
import { useAuth } from '../auth/AuthProvider';
import { api, qs } from '../api/client';
import { Icon } from '../ui/Icon';
import { useTheme } from '../ui/useTheme';
import logo from '../assets/tww-logo.svg';

const NAV: { to: string; label: string; icon: string; module: ModuleKey }[] = [
  { to: '/', label: 'Übersicht', icon: 'grid', module: 'dashboard' },
  { to: '/aktionen', label: 'Aktionen', icon: 'tasks', module: 'actions' },
  { to: '/flaechen', label: 'Flächen', icon: 'map', module: 'areas' },
  { to: '/monitoring', label: 'Monitoring', icon: 'chart', module: 'monitoring' },
  { to: '/kontakte', label: 'Kontakte', icon: 'users', module: 'contacts' },
  { to: '/spendenpartner', label: 'Spendenpartner', icon: 'building', module: 'contacts' },
  { to: '/bilanz', label: 'Jahresbilanz', icon: 'bars', module: 'actions' },
];

export function AppShell() {
  const { me, can, logout } = useAuth();
  const { isDark, toggle } = useTheme();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const user = me!.user;
  const roleLabel = me!.roles.map((r) => ROLE_NAMES[r]).join(', ') || 'keine Rolle';
  return (
    <div className="app">
      {open && <div className="side-bg" onClick={() => setOpen(false)} />}
      <aside className={`side ${open ? 'open' : ''}`}>
        <Link className="brand brand-logo" to="/" onClick={() => setOpen(false)} aria-label="Projektdatenbank Trinkwasserwald e.V., Übersicht" title="Übersicht">
          <img src={logo} alt="Trinkwasserwald e.V." />
        </Link>
        {NAV.filter((n) => can(n.module)).map((n) => (
          <NavLink key={n.to} to={n.to} end={n.to === '/'} className={({ isActive }) => `nav ${isActive ? 'active' : ''}`} onClick={() => setOpen(false)}>
            <Icon name={n.icon} />
            {n.label}
          </NavLink>
        ))}
        {(can('users') || can('settings') || can('templates')) && (
          <NavLink to="/verwaltung" className={({ isActive }) => `nav ${isActive ? 'active' : ''}`} onClick={() => setOpen(false)}>
            <Icon name="settings" />
            Verwaltung
          </NavLink>
        )}
        <div className="grow" />
        <div className="user">
          <div className="av">{user.initials || initialsOf(user.display_name)}</div>
          <div className="nm">
            {user.display_name}
            <br />
            <span style={{ color: 'var(--muted)' }}>{roleLabel}</span>
          </div>
        </div>
      </aside>
      <div className="main">
        <div className="top">
          <button className="icon-btn menu-btn" onClick={() => setOpen(true)} aria-label="Menü" type="button">
            <Icon name="menu" />
          </button>
          <GlobalSearch onGo={(href) => nav(href)} />
          <div style={{ flex: 1 }} />
          <button className="icon-btn" onClick={toggle} aria-label={isDark ? 'Helles Design' : 'Dunkles Design'} title={isDark ? 'Helles Design' : 'Dunkles Design'} type="button">
            <Icon name={isDark ? 'sun' : 'moon'} />
          </button>
          <Link to="/profil" className="icon-btn" aria-label="Profil" title="Profil">
            <Icon name="user" />
          </Link>
          <button className="icon-btn" onClick={logout} aria-label="Abmelden" title="Abmelden" type="button">
            <Icon name="logout" />
          </button>
        </div>
        <main className="view">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function GlobalSearch({ onGo }: { onGo: (href: string) => void }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const ref = useRef<HTMLInputElement>(null);
  const r = useQuery({
    queryKey: ['search', q],
    queryFn: () => api.get<SearchResponse>(`/search${qs({ q })}`),
    enabled: q.trim().length >= 2,
    staleTime: 10_000,
  });
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === '/' && !(e.target as HTMLElement)?.closest('input, textarea, select, [contenteditable]')) {
        e.preventDefault();
        ref.current?.focus();
      }
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, []);
  const hits = r.data ? [...r.data.contacts, ...r.data.areas, ...r.data.actions] : [];
  const go = (href: string) => {
    onGo(href);
    setQ('');
    setOpen(false);
    ref.current?.blur();
  };
  return (
    <div className="search" onBlur={(e) => !e.currentTarget.contains(e.relatedTarget as Node) && setOpen(false)}>
      <Icon name="search" />
      <input
        ref={ref}
        value={q}
        placeholder="Suchen: Aktion, Fläche, Kontakt (Taste /)"
        aria-label="Globale Suche"
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
          setSel(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') setSel((s) => Math.min(hits.length - 1, s + 1));
          else if (e.key === 'ArrowUp') setSel((s) => Math.max(0, s - 1));
          else if (e.key === 'Enter' && hits[sel]) go(hits[sel]!.href);
          else if (e.key === 'Escape') setOpen(false);
        }}
      />
      {open && q.trim().length >= 2 && (
        <div className="results">
          {r.data && (['contacts', 'areas', 'actions'] as const).map((g) => {
            const list = r.data![g];
            if (!list.length) return null;
            return (
              <div key={g}>
                <div className="grp">{{ contacts: 'Kontakte', areas: 'Flächen', actions: 'Aktionen' }[g]}</div>
                {list.map((h) => (
                  <a key={`${h.type}${h.id}`} href={h.href} className={hits[sel] === h ? 'sel' : ''} onMouseDown={(e) => (e.preventDefault(), go(h.href))}>
                    {h.title}
                    <span>{h.sub}</span>
                  </a>
                ))}
              </div>
            );
          })}
          {r.data && hits.length === 0 && <div className="none">Keine Treffer für „{q}“</div>}
          {r.isLoading && <div className="none">Suche …</div>}
        </div>
      )}
    </div>
  );
}
